import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { getParentProjectType } from '@/features/org/utils/project-utils';
import { AcceptInvitationHeader } from '@/features/partners/components/layouts/accept-partner-invitation-header';
import { useAcceptSubprojectInvitationStore } from '@/features/partners/store/use-accept-subproject-invitation-store';
import { useProjectKpis } from '@/features/projects/project-details/hooks/use-project-kpis';
import {
  FlowBackButton,
  FlowSkipStepButton,
  FlowSubmitButton,
} from '@/shared/components/flow/flow-actions';
import {
  FlowFormFields,
  FlowHeaderOptional,
} from '@/shared/components/flow/flow-fields';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import { getKpiUnitTranslationPlural } from '@/shared/hooks/use-kpi-unit-translation';
import { Project } from '@/shared/types/api';
import {
  formatNumberWithDots,
  parseFormattedNumber,
} from '@/shared/utils/format';

export const AcceptInvitationBudgetPage = ({
  clientSubproject,
  kpisIds,
}: {
  clientSubproject: Project;
  kpisIds: number[];
}) => {
  const { t } = useTranslation('subproject', {
    keyPrefix: 'AcceptInvitation.BudgetForm',
  });
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });

  const { data: acceptData, setData: setAcceptData } =
    useAcceptSubprojectInvitationStore();

  const projectId = clientSubproject!.parent_id;
  const { data: kpis, isLoading: isLoadingKpis } = useProjectKpis({
    projectId: Number(projectId),
    projectType: getParentProjectType(clientSubproject.type),
  });

  const relevantKpis = kpis?.filter((kpi) => kpisIds.includes(kpi.id)) || [];

  // Dynamic form schema based on KPIs
  const formSchema = z.object(
    relevantKpis.reduce(
      (acc, kpi) => {
        acc[`budget_${kpi.id}`] = z.string().optional();
        return acc;
      },
      {} as Record<string, z.ZodString | z.ZodOptional<z.ZodString>>,
    ),
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: relevantKpis.reduce(
      (acc, kpi) => {
        acc[`budget_${kpi.id}`] =
          formatNumberWithDots(acceptData.kpiBudgets?.[kpi.id]) || '';
        return acc;
      },
      {} as Record<string, string>,
    ),
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    const kpiBudgets: Record<number, number | null> = {};

    relevantKpis.forEach((kpi) => {
      kpiBudgets[kpi.id] = null;
      const budgetValue = values[`budget_${kpi.id}`];
      if (budgetValue && budgetValue.trim() !== '') {
        const numericValue = parseFormattedNumber(budgetValue);
        if (!isNaN(numericValue)) {
          kpiBudgets[kpi.id] = numericValue;
        }
      }
    });

    setAcceptData({
      kpiBudgets,
      step: 'inviteMembersOverview',
    });
  }

  const handleSkip = () => {
    setAcceptData({
      step: 'inviteMembersOverview',
    });
  };

  useEffect(() => {
    if (isLoadingKpis) {
      return;
    }
    if (relevantKpis.length === 0) {
      setAcceptData({ step: 'inviteMembersOverview' });
    }
  }, [relevantKpis, isLoadingKpis]);

  return (
    <>
      <AcceptInvitationHeader>
        <FlowSkipStepButton onClick={handleSkip} />
      </AcceptInvitationHeader>
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <FlowHeaderOptional />
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              {isLoadingKpis ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <FlowFormFields>
                  {relevantKpis.map((kpi) => (
                    <div
                      key={kpi.id}
                      className="grid grid-cols-2 items-center gap-10"
                    >
                      <LabelInput
                        label={t('kpi_label')}
                        disabled
                        value={`${kpi.name} ${getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}`}
                      />
                      <FormField
                        control={form.control}
                        name={`budget_${kpi.id}`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <LabelInput
                                label={t('budget_label')}
                                placeholder="0"
                                value={field.value}
                                onChange={(e) => {
                                  const formatted = formatNumberWithDots(
                                    parseFormattedNumber(e.target.value),
                                  );
                                  field.onChange(formatted);
                                }}
                                onBlur={field.onBlur}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                </FlowFormFields>
              )}
            </CardContent>
            <CardFooter>
              <FlowBackButton
                onClick={() => {
                  setAcceptData({
                    step: 'subcategory',
                  });
                }}
              >
                {t('back_button_label')}
              </FlowBackButton>
              <FlowSubmitButton disabled={isLoadingKpis}>
                {t('submit_button_label')}
              </FlowSubmitButton>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
